import React from 'react';
import classnames from 'classnames';
import * as IoC from 'typescript-ioc';
import { IHubAttendeeListItemDTO } from '../../../../../api/event-hubs.dao';
import Text from '../../../../global/text/text';
import DefaultButton from '../../../../global/default-button/default-button';
import { editText } from '../../../../../constants/common-labels.constants';
import { AttendeeDetailsPopover } from '../../../../global/attendee-column/attendee-column-popover/attendee-column-popover';
import { Permission } from '../../../../../api/roles.dao';
import { DateTimeFormatter } from '../../../../../infrastructure/implementations/date-formatter';
import { THubAttendeeColumnId } from '../hub-attendee-columns';

interface IHubAttendeesListItemProps {
    attendee: IHubAttendeeListItemDTO;
    onEdit: () => void;
    isEditable: boolean;
    canEditHubOrganization?: boolean;
    onEditOrganization?: () => void;
    visibleColumnIds: THubAttendeeColumnId[];
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const HubAttendeesListItem = (props: IHubAttendeesListItemProps) => {
    const { attendee, onEdit, isEditable, canEditHubOrganization = false, onEditOrganization, visibleColumnIds } = props;

    const {
        firstName,
        lastName,
        email,
        organizationName,
        organizationType,
        organizationId,
        title,
        phoneNumber,
        salutation,
        assistantEmail,
        accessCode,
        externalAttendeeId,
        registrationDate,
        registrationStatus,
        customFieldValues,
    } = attendee;

    const attendeeName = [firstName, lastName].filter(Boolean).join(' ');
    const cellEffectClasses = classnames({
        'hoverable': true
    });

    const attendeeInfo = {
        name: attendeeName,
        organizationName: organizationName || '',
        title: title || '',
        email: email || '',
        tel: phoneNumber || '',
    };

    const [formattedRegistrationDate, formattedRegistrationTime] = registrationDate
        ? dateTimeFormatter.dateToLocalString(new Date(registrationDate)).split(',')
        : ['', ''];

    const renderColumnCell = (columnId: THubAttendeeColumnId): React.ReactNode => {
        switch (columnId) {
            case 'name':
                return (
                    <td key={columnId} className={classnames('attendee-name overflow-ellipsis', cellEffectClasses)}>
                        <AttendeeDetailsPopover
                            attendeeInfo={attendeeInfo}
                            onEdit={onEdit}
                            editPermission={Permission.EDIT_HUB_ATTENDEES}
                        >
                            <Text tag='div' className='attendee-list-item__text attendee-list-item__text--with-hover' message={attendeeName} withEllipsis escapeHtml={true} />
                        </AttendeeDetailsPopover>
                    </td>
                );
            case 'email':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={email || ''} withEllipsis />;
            case 'organization':
                return (
                    <td key={columnId} className={classnames(cellEffectClasses)}>
                        <div className='flex center'>
                            <Text withEllipsis message={organizationName || ''} escapeHtml={true} />
                            {canEditHubOrganization && organizationId != null && onEditOrganization && (
                                <DefaultButton
                                    className='visible-while-hovered'
                                    modifier={['slim', 'link']}
                                    text={editText()}
                                    onClick={() => onEditOrganization()}
                                />
                            )}
                        </div>
                    </td>
                );
            case 'organizationType':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={organizationType || ''} withEllipsis escapeHtml={true} />;
            case 'title':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={title || ''} withEllipsis escapeHtml={true} />;
            case 'phoneNumber':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={phoneNumber || ''} withEllipsis escapeHtml={true} />;
            case 'salutation':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={salutation || ''} withEllipsis escapeHtml={true} />;
            case 'assistantEmail':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={assistantEmail || ''} withEllipsis />;
            case 'accessCode':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={accessCode || ''} withEllipsis />;
            case 'externalAttendeeId':
                return <Text key={columnId} tag='td' className={cellEffectClasses} message={externalAttendeeId || ''} withEllipsis />;
            case 'registrationDate':
                return (
                    <td key={columnId} className={cellEffectClasses}>
                        <p>{formattedRegistrationDate.trim()}</p>
                        <p>{formattedRegistrationTime.trim()}</p>
                    </td>
                );
            case 'registrationStatus':
                return (
                    <Text
                        key={columnId}
                        tag='td'
                        className={cellEffectClasses}
                        message={registrationStatus || ''}
                        id={registrationStatus ? `oe.registrationStatus.${registrationStatus}` : undefined}
                        withEllipsis
                    />
                );
            default:
                // Remaining columns are custom fields, their id is the custom field id.
                return <Text key={columnId} tag='td'
                    className={cellEffectClasses}
                    message={customFieldValues?.[Number(columnId)] ?? ''}
                    withEllipsis
                    escapeHtml={true}
                />;
        }
    };

    return (
        <tr className='attendees-list-item' onDoubleClick={() => isEditable && onEdit()}>
            {visibleColumnIds.map(renderColumnCell)}
            <td className={cellEffectClasses} style={{ width: 72 }} />
            <td className={classnames('align-right', cellEffectClasses)} style={{ width: 80 }}>
                {isEditable &&
                    <DefaultButton
                        className='visible-while-hovered'
                        onClick={() => onEdit()}
                        modifier='slim'
                        text={editText()}
                    />
                }
            </td>
        </tr>
    );
};

export { HubAttendeesListItem };
